import { Card, CardContent, Divider, Grid, Menu, MenuItem, Typography } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import * as coreApi from "api/Core";
import * as api from "api/Knowledge";
import { FeedClassKey, GlobalClassKeys } from "assets/jss/common-styles";
import ButtonEllipsis from "components/Buttons/ButtonEllipsis";
import ButtonFollow from "components/Buttons/ButtonFollow";
import CardButtonShare from "components/Buttons/CardButtonShare";
import IconButtonEdit from "components/Buttons/IconButtonEdit";
import IconButtonNotify from "components/Buttons/IconButtonNotify";
import IconButtonSilience from "components/Buttons/IconButtonSilience";
import { RequestWrapIndicator } from "components/CallServer/RequestWrapIndicator";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiImage from "components/MuiExtras/MuiImage";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils from "lib/LibUtils";
import { FeedTopicDto, MemberDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import { RoutePaths } from "routes/RouteConfig";
import Session from "services/Session";
import TopicRequestKnowlegeTestDrawer from "views/Timeline/Dialogs/TopicRequestKnowlegeTestDrawer";
import TopicShareDrawer from "views/Timeline/Dialogs/TopicShareDrawer";
import TopicFollowersDrawer from "views/Topic/Dialogs/TopicFollowersDrawer";
import TopicRelationsDrawer from "views/Topic/Dialogs/TopicRelationsDrawer";
import TopicStatisticsDrawer from "views/Topic/Dialogs/TopicStatisticsDrawer";
import TopicWhoKnowsAboutDrawer from "views/Topic/Dialogs/TopicWhoKnowsAboutDrawer";
import { DynamicSystemRoles, SystemRoles } from "models/ApiEnums";
import TopicRelationshipsDialog from "views/Topic/Dialogs/TopicRelationshipsDialog";
import CardButtonSeeAlso from "components/Buttons/CardButtonSeeAlso";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    menuAnchorEl: any;
    mouseOver: boolean;
    updating: boolean;
    dialog: any;
}

interface IProps {
    topic: FeedTopicDto;
    onUpdateCard: (item: DeepPartial<FeedTopicDto>) => void;
    onRelationshipChanged: () => void;
    showRelationsButton: boolean;
    onInfoRequest?: () => void;
}

type Props = IProps & RouteComponentProps & WithSnackbar & WithTranslation & StyledComponentProps<FeedClassKey>;

/////////////////////////////////////////////////////////////////////////////////////

class TopicCardHeader extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
            updating: false,
            mouseOver: false,
            menuAnchorEl: null,
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Topic.Components.TopicCardHeader." + r, f);
    }

    ///
    private handleEdit = () => {
        this.setState({
            mouseOver: false,
            updating: true,
            menuAnchorEl: null,
        });
        this.props.history.push(LibUtils.replaceRouterParameter(RoutePaths.TopicAdminList, [this.props.topic.topicId]));
    };

    ///
    private handleSilience = () => {
        const { topic: { topicId, interaction: { inBlackList } } } = this.props;

        var message = this.res(
            inBlackList
                ? "ConfirmationUnmuteMessage"
                : "ConfirmationMuteMessage"
        );

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <MuiConfirmation
                    message={message}
                    onClose={
                        (result) => {
                            if (result) {

                                var action = inBlackList
                                    ? coreApi.userUnmuteTopic(topicId)
                                    : coreApi.userMuteTopic(topicId);

                                action
                                    .then(() => {

                                        this.setState({ dialog: null }, () =>
                                            this.props.onUpdateCard({
                                                topicId,
                                                interaction: {
                                                    inBlackList: !inBlackList,
                                                }
                                            })
                                        );

                                        if (inBlackList)
                                            this.props.snackbar.showMessage(this.res("UnmuteSuccess"), "success");
                                        else
                                            this.props.snackbar.showMessage(this.res("MuteSuccess"), "warning");
                                    })
                                    .catch(() => {
                                        this.setState({ dialog: null });
                                    });
                            }
                            else {
                                this.setState({ dialog: null });
                            }
                        }
                    }
                />
            )
        });
    };

    ///
    private handleNotification = () => {
        const { topic: { topicId, interaction: { notifyChanges } } } = this.props;

        coreApi.setFollowedTopicNotification(topicId, !notifyChanges)
            .then((newInteration) => {

                this.props.onUpdateCard({
                    topicId,
                    interaction: {
                        following: newInteration.following,
                        notifyChanges: newInteration.notifyChanges,
                    }
                });

                if (newInteration.notifyChanges)
                    this.props.snackbar.showMessage(this.res("TopicWillReceiveNotifySuccess"), "success");
                else
                    this.props.snackbar.showMessage(this.res("TopicWontReceiveNotifySuccess"), "warning");

                this.setState({ dialog: null });

            })
            .catch((error) => {
                this.setState({ dialog: null });
            });
    };

    ///
    private handleFollow = () => {
        const { topic: { topicId, interaction: { following } } } = this.props;

        coreApi.followTopic(topicId, !following)
            .then((result) => {

                this.props.onUpdateCard({
                    topicId,
                    interaction: {
                        following: result.following,
                        notifyChanges: result.notifyChanges,
                    }
                });

                if (result.following)
                    this.props.snackbar.showMessage(this.res("FollowingSuccess"), "success");
                else
                    this.props.snackbar.showMessage(this.res("UnfollowingSuccess"), "warning");
            });
    };

    ///
    private handleCloseMenuOverflow = () => {
        this.setState({ menuAnchorEl: null });
    };

    ///
    private handleCloseDialog = () => {
        this.setState({ dialog: null });
    };

    ///
    private handleShare = () => {
        this.setState({
            dialog:
                <TopicShareDrawer
                    topicId={this.props.topic.topicId}
                    hasMemberRestriction={!this.props.topic.isPublic}
                    onClose={(result) => {
                        if (result) {
                            api.shareTopic({
                                topicId: this.props.topic.topicId,
                                members: result.members.map((v) => { return { memberId: v.key, memberType: v.memberType } as MemberDto; }),
                                message: result.comment,
                            }).then((count) => {
                                if (count == 0)
                                    this.props.snackbar.showMessage(this.res("ShareTopicFailed"), "error");
                                else
                                    this.props.snackbar.showMessage(this.res("ShareTopicSuccess", { count }), "success");
                            });
                        }
                        this.setState({ dialog: null });
                    }}
                />
        });
    };

    ///
    private handleRequestedKnowledgeTest = () => {
        this.setState({
            menuAnchorEl: null,
            dialog:
                <TopicRequestKnowlegeTestDrawer
                    topicId={this.props.topic.topicId}
                    onClose={
                        (result) => {
                            if (result) {
                                api.topicRequireKnowledgeTest({
                                    topicId: this.props.topic.topicId,
                                    members: result.members.map((v) => { return { memberId: v.key, memberType: v.memberType } as MemberDto; }),
                                    message: result.comment,
                                }).then(() => {
                                    this.props.snackbar.showMessage(this.res("RequestKnowlegeSuccess"), "success");
                                });
                            }
                            this.setState({ dialog: null });
                        }}
                />
        });
    };

    ///
    private handleWhoKnowsAboutDrawer = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <TopicWhoKnowsAboutDrawer
                topicId={this.props.topic.topicId}
                onClose={this.handleCloseDialog}
            />
        });
    };

    ///
    private handleRelationsDrawer = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <TopicRelationsDrawer
                topicId={this.props.topic.topicId}
                onClose={this.handleCloseDialog}
            />
        });
    };

    ///
    private handleFollowersDrawer = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <TopicFollowersDrawer
                topicId={this.props.topic.topicId}
                onClose={this.handleCloseDialog}
            />
        });
    };

    ///
    private handleRelationshipClose = (changed: boolean) => {
        this.setState({ dialog: null });
        if (changed) this.props.onRelationshipChanged();
    };

    ///
    private handleRelationshipDialog = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <TopicRelationshipsDialog
                topicId={this.props.topic.topicId}
                topicName={this.props.topic.name}
                onClose={this.handleRelationshipClose}
            />
        });
    };

    ///
    private handleRelations = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: (
                <TopicRelationsDrawer
                    topicId={this.props.topic.topicId}
                    onClose={this.handleCloseDialog}
                />
            )
        });
    };

    //
    private handleInfo = () => {
        this.setState({ menuAnchorEl: null }, () => this.props.onInfoRequest());
    };

    ///
    private handleStatsDrawer = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <TopicStatisticsDrawer
                topicId={this.props.topic.topicId}
                onClose={this.handleCloseDialog}
            />
        });
    };

    ///
    private buildOverflowMenu(): Array<any> {
        const {
            topic: {
                permissions,
                containsTests,
            }
        } = this.props;

        const actionMenu: Array<any> = [];

        if (permissions.canEdit) {
            actionMenu.push(<MenuItem key="edit" onClick={this.handleEdit}>{this.props.t("Views.Topic.Common.Actions.Edit")}</MenuItem>);
            actionMenu.push(<MenuItem key="m5" onClick={this.handleRelationshipDialog}>{this.props.t("Views.Topic.Common.Actions.Relationships")}</MenuItem >);

            if (containsTests && Session.hasDynamicRole(DynamicSystemRoles.Manager))
                actionMenu.push(<MenuItem key="ktest" onClick={this.handleRequestedKnowledgeTest}>{this.props.t("Views.Topic.Common.Actions.RequestKnowledgeTest")}</MenuItem >);
        }

        if (this.props.topic.interaction.inBlackList === false) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="div1" />);
            actionMenu.push(
                <MenuItem key="silience" onClick={this.handleSilience}>
                    <span>{this.props.t("Views.Topic.Common.Actions.Mute")}</span>
                </MenuItem>
            );
        }

        if (actionMenu.length > 0) actionMenu.push(<Divider key="div2" />);
        actionMenu.push(<MenuItem key="m1" onClick={this.handleWhoKnowsAboutDrawer}>{this.props.t("Views.Topic.Common.Actions.WhoKnowsAbout")}</MenuItem>);
        actionMenu.push(<MenuItem key="m2" onClick={this.handleFollowersDrawer}>{this.props.t("Views.Topic.Common.Actions.Followers")}</MenuItem>);
        actionMenu.push(<MenuItem key="m3" onClick={this.handleRelationsDrawer}>{this.props.t("Views.Topic.Common.Actions.Relations")}</MenuItem>);
        actionMenu.push(<MenuItem key="m4" onClick={this.handleStatsDrawer}>{this.props.t("Views.Topic.Common.Actions.Statistics")}</MenuItem >);

        if (this.props.onInfoRequest) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="div3" />);
            actionMenu.push(<MenuItem key="info" onClick={this.handleInfo}><span>{this.props.t("Common.MenuAction.Info")}</span></MenuItem>);
        }

        return actionMenu;
    }

    ///
    public render() {
        const {
            classes,
            topic
        } = this.props;

        const {
            updating,
            mouseOver
        } = this.state;

        const overflowActions = this.buildOverflowMenu();

        return (
            <>
                {this.state.dialog}

                <Card className={classes.card}
                    onMouseEnter={() => this.setState({ mouseOver: true })}
                    onMouseLeave={() => this.setState({ mouseOver: false })
                    }
                >
                    <RequestWrapIndicator enabled={updating} type={"post"}>

                        <CardContent>
                            <Grid container style={{ width: "auto" }} spacing={2} wrap="nowrap">
                                {
                                    <Grid item>
                                        <MuiImage orientation="square" src={topic.thumbnailUrl} />
                                    </Grid>
                                }
                                <Grid item className={GlobalClassKeys.grow}>

                                    <MuiContainer margin="dense">
                                        <Grid container justifyContent="space-between" wrap="nowrap">
                                            <Grid item>
                                                <Typography component="div" variant="h6">{topic.name}</Typography>

                                                <MuiContainer margin="dense">
                                                    {
                                                        topic.description != null &&
                                                        <Typography component="div" variant="body2" >
                                                            <MuiMuted>
                                                                <span dangerouslySetInnerHTML={{ __html: HtmlUtils.textToHtml(topic.description) }} ></span>
                                                            </MuiMuted>
                                                        </Typography>
                                                    }
                                                </MuiContainer>

                                                {
                                                    topic.lastPublication &&
                                                    <Typography component="div" variant="body2">
                                                        <MuiMuted>
                                                            <b>{this.res("LastPublicationText", { lastPublication: topic.lastPublication })}</b>
                                                        </MuiMuted>
                                                    </Typography>
                                                }
                                            </Grid>
                                            {
                                                topic.permissions.canEdit &&
                                                <Grid item>
                                                    <IconButtonEdit
                                                        size="small"
                                                        visible={mouseOver}
                                                        onClick={this.handleEdit}
                                                    />
                                                </Grid>
                                            }
                                        </Grid>
                                    </MuiContainer>

                                </Grid>
                            </Grid>

                            <Grid container
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item>
                                    {
                                        (topic.permissions.canFollow || topic.interaction.following)
                                        &&
                                        <ButtonFollow
                                            following={topic.interaction.following}
                                            onClick={this.handleFollow}
                                        />
                                    }

                                    {
                                        topic.interaction.following && topic.interaction.inBlackList == false &&
                                        <IconButtonNotify
                                            color={topic.interaction.notifyChanges ? "success" : "muted"}
                                            active={topic.interaction.notifyChanges}
                                            titleActive={this.res("NotificationActiveTitle")}
                                            titleInactive={this.res("NotificationInactiveTitle")}
                                            onClick={this.handleNotification}
                                        />
                                    }

                                    {
                                        topic.interaction.inBlackList == true &&
                                        <IconButtonSilience
                                            active={topic.interaction.inBlackList}
                                            titleActive={this.res("MuteActiveTitle")}
                                            titleInactive={this.res("MuteInactiveTitle")}
                                            onClick={this.handleSilience}
                                        />
                                    }

                                </Grid>

                                <Grid item className={classes.cardActions}>

                                    <CardButtonShare onClick={this.handleShare} />

                                    {
                                        this.props.showRelationsButton && topic.relatedTopics.length > 0
                                            ? (
                                                <CardButtonSeeAlso
                                                    color={"pressed"}
                                                    title={this.props.t("Views.Topic.Widgets.TopicRelationsWidget.BoxTitle")}
                                                    onClick={this.handleRelations}
                                                />
                                            )
                                            : null
                                    }

                                    {
                                        overflowActions.length > 0 &&
                                        <>
                                            <ButtonEllipsis
                                                onClick={(e) => { this.setState({ menuAnchorEl: e.currentTarget }); }}
                                            />
                                            <Menu
                                                open={this.state.menuAnchorEl != null}
                                                anchorEl={this.state.menuAnchorEl}
                                                onClose={this.handleCloseMenuOverflow}
                                            >
                                                {overflowActions}
                                            </Menu>
                                        </>
                                    }

                                </Grid>

                            </Grid>
                        </CardContent>

                    </RequestWrapIndicator>
                </Card>
            </>
        );
    }
}

export default withRouter(withSnackbar(withTranslation()(TopicCardHeader)));